import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { cn, errorHandler } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { LayoutList, LinkIcon, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { deleteEntryPoint, patchEntryPointCustomSlug, patchEntryPointName, toggleEntryPoint, } from '@/services/entry-point';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { QrCustomizeDialog } from '@/components/dialog/qr-customize-Dialog';
export const EntryLinkListDialog = ({ entryPoints, isIcon, onCopy, onSuccess }) => {
    const { t } = useTranslation();
    const { workspace } = useAuthenticationState();
    const allowCustomSlug = Boolean(workspace?.subscription?.plan?.allowCustomSlug);
    const [open, setOpen] = React.useState(false);
    const [editingId, setEditingId] = React.useState(null);
    const [draftName, setDraftName] = React.useState('');
    const [editingSlugId, setEditingSlugId] = React.useState(null);
    const [draftSlug, setDraftSlug] = React.useState('');
    const patchNameMutation = useMutation({
        mutationFn: patchEntryPointName,
        onSuccess: (data) => {
            toast.success(data.message);
            onSuccess?.(data.entryPoint);
        },
        onError: errorHandler,
    });
    const patchSlugMutation = useMutation({
        mutationFn: patchEntryPointCustomSlug,
        onSuccess: (data) => {
            toast.success(data.message);
            setEditingSlugId(null);
            setDraftSlug('');
            onSuccess?.(data.entryPoint);
        },
        onError: errorHandler,
    });
    const toggleMutation = useMutation({
        mutationFn: toggleEntryPoint,
        onSuccess: (data) => {
            toast.success(data.message);
            onSuccess?.(data.entryPoint);
        },
        onError: errorHandler,
    });
    const deleteMutation = useMutation({
        mutationFn: deleteEntryPoint,
        onSuccess: (data) => {
            toast.success(data.message);
            onSuccess?.(data.entryPoint);
        },
        onError: errorHandler,
    });
    const startEdit = (entryPoint) => {
        setEditingId(entryPoint.publicId);
        setDraftName(entryPoint.name ?? '');
    };
    const cancelEdit = React.useCallback(() => {
        setEditingId(null);
        setDraftName('');
        setEditingSlugId(null);
        setDraftSlug('');
    }, []);
    const startSlugEdit = (entryPoint) => {
        setEditingSlugId(entryPoint.publicId);
        setDraftSlug(entryPoint.customSlug ?? '');
    };
    const applySlugEdit = (entryPoint) => {
        const trimmed = draftSlug.trim();
        // Empty input → clear (server allows it regardless of plan).
        patchSlugMutation.mutate({
            publicId: entryPoint.publicId,
            customSlug: trimmed === '' ? null : trimmed,
        });
    };
    const applyEdit = async (entryPoint) => {
        const next = draftName.trim();
        if (next.length === 0) {
            toast.error(t('required.entry_points.name', { ns: 'validation' }));
            return;
        }
        try {
            patchNameMutation.mutate({ publicId: entryPoint.publicId, name: next });
            cancelEdit();
        }
        catch (_) {
            toast.error(t('occurred', { ns: 'error' }));
        }
    };
    React.useEffect(() => {
        if (!open)
            cancelEdit();
    }, [open, cancelEdit]);
    const defaultEp = entryPoints.find((entryPoint) => entryPoint.isDefault);
    const customEps = entryPoints.filter((entryPoint) => !entryPoint.isDefault);
    const isEditingRow = editingId !== null;
    return (<Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {isIcon ? (<Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => setOpen((prevState) => !prevState)}>
                                <LayoutList />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t('entry_points.list.title', { ns: 'glossary' })}</p>
                        </TooltipContent>
                    </Tooltip>) : (<Button size="sm">{t('entry_points.list.title', { ns: 'glossary' })}</Button>)}
            </DialogTrigger>

            <DialogContent className={cn('w-[95vw] max-w-[95vw] max-h-[85vh] overflow-y-auto', 'sm:w-180 sm:max-w-180', 'md:w-215 md:max-w-215')}>
                <DialogHeader>
                    <DialogTitle>{t('entry_points.list.title', { ns: 'glossary' })}</DialogTitle>
                    <DialogDescription>{t('entry_points.list.help', { ns: 'glossary' })}</DialogDescription>
                </DialogHeader>

                {/* Default link block (kept separate, simple) */}
                {defaultEp && (<div className="rounded-lg border border-gray-800 p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-semibold">
                                        {t('entry_points.default.title', { ns: 'glossary' })}
                                    </p>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-gray-700 text-gray-400">
                                        {t('system')}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-400 truncate">
                                    {defaultEp.context?.label ?? defaultEp.context?.keyName ?? t('baseline')}
                                </p>
                            </div>

                            <Button variant="outline" size="sm" onClick={() => onCopy?.(defaultEp)}>
                                {t('copy_link')}
                            </Button>
                        </div>

                        <p className="mt-2 text-xs text-gray-500">
                            {t('entry_points.default.help', { ns: 'glossary' })}
                        </p>
                    </div>)}

                {/* Table */}
                <div className="rounded-lg border border-gray-800 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[38%]">{t('name')}</TableHead>
                                <TableHead className="w-[42%]">{t('context')}</TableHead>
                                <TableHead className="w-[10%] text-center">{t('status')}</TableHead>
                                <TableHead className="w-[10%] text-right">{t('actions')}</TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {customEps.length === 0 ? (<TableRow>
                                    <TableCell colSpan={4} className="py-8 text-center text-sm text-gray-500">
                                        {t('entry_points.list.empty', { ns: 'glossary' })}
                                    </TableCell>
                                </TableRow>) : (customEps.map((entryPoint) => (<TableRow key={entryPoint.publicId}>
                                        <TableCell className="align-top">
                                            {editingId === entryPoint.publicId ? (<div className="space-y-2">
                                                    <input value={draftName} onChange={(e) => setDraftName(e.target.value)} className={cn('w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm', 'focus:outline-none focus:ring-2 focus:ring-primary')} autoFocus/>

                                                    <div className="flex items-center gap-2">
                                                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                                                            {t('reset', { defaultValue: 'Reset' })}
                                                        </Button>

                                                        <Button size="sm" onClick={() => applyEdit(entryPoint)} disabled={patchNameMutation.isPending || draftName.length === 0}>
                                                            {t('apply', { defaultValue: 'Apply' })}
                                                        </Button>
                                                    </div>
                                                </div>) : (<div className="space-y-1">
                                                    <button type="button" className={cn('font-medium leading-tight text-left w-full', 'hover:underline underline-offset-2')} onClick={() => startEdit(entryPoint)}>
                                                        {entryPoint.name ??
                    entryPoint.context?.label ??
                    entryPoint.publicId}
                                                    </button>

                                                    <p className="text-xs text-gray-500 break-all">
                                                        {'EP-' + entryPoint.publicId}
                                                    </p>

                                                    {/* Custom URL inline editor / display. Plan-gated:
                    FREE workspaces see a locked badge linking to billing
                    instead of a clickable add/edit affordance. */}
                                                    {editingSlugId === entryPoint.publicId ? (<div className="space-y-1 pt-1">
                                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                                                <span>/c/</span>
                                                                <input value={draftSlug} onChange={(e) => setDraftSlug(e.target.value)} placeholder="my-vanity-url" className={cn('flex-1 rounded-md border border-gray-700 bg-transparent px-2 py-1 text-xs', 'focus:outline-none focus:ring-2 focus:ring-primary')} autoFocus/>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Button size="sm" variant="outline" onClick={() => {
                        setEditingSlugId(null);
                        setDraftSlug('');
                    }}>
                                                                    {t('cancel')}
                                                                </Button>
                                                                <Button size="sm" onClick={() => applySlugEdit(entryPoint)} disabled={patchSlugMutation.isPending}>
                                                                    {t('apply', { defaultValue: 'Apply' })}
                                                                </Button>
                                                            </div>
                                                        </div>) : entryPoint.customSlug ? (<button type="button" className="text-xs text-primary hover:underline underline-offset-2 break-all text-left" onClick={allowCustomSlug
                        ? () => startSlugEdit(entryPoint)
                        : undefined} disabled={!allowCustomSlug}>
                                                            /c/{entryPoint.customSlug}
                                                        </button>) : allowCustomSlug ? (<button type="button" className="text-[11px] text-gray-500 hover:text-primary hover:underline underline-offset-2" onClick={() => startSlugEdit(entryPoint)}>
                                                            {t('entry_points.custom_slug.add', {
                        ns: 'glossary',
                        defaultValue: '+ Custom URL',
                    })}
                                                        </button>) : null}
                                                </div>)}
                                        </TableCell>

                                        <TableCell className="align-top">
                                            <div className="space-y-1">
                                                <p className="text-sm text-gray-300">
                                                    {entryPoint.context?.label ??
                entryPoint.context?.label ??
                t('unknown')}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {(entryPoint.context?.channel?.label ??
                entryPoint.context?.channel?.keyName ??
                'Channel') +
                ' · ' +
                (entryPoint.context?.placement?.label ??
                    entryPoint.context?.placement?.keyName ??
                    'Placement')}
                                                </p>
                                            </div>
                                        </TableCell>

                                        <TableCell className="align-top text-center">
                                            <div className="flex items-center justify-center w-full h-full">
                                                <Switch checked={entryPoint.isActive} onCheckedChange={() => toggleMutation.mutate(entryPoint.publicId)} aria-label={t('toggle')} disabled={isEditingRow}/>
                                            </div>
                                        </TableCell>

                                        <TableCell className="align-top text-right">
                                            <div className="flex justify-end gap-1">
                                                <QrCustomizeDialog entryPoint={entryPoint} isIcon/>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button size="icon" variant="ghost" onClick={() => onCopy?.(entryPoint)} aria-label={t('copy_link')} disabled={isEditingRow}>
                                                            <LinkIcon className="h-4 w-4"/>
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('copy_link')}</TooltipContent>
                                                </Tooltip>

                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <AlertDialog>
                                                            <AlertDialogTrigger asChild>
                                                                <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-600" aria-label={t('delete')} disabled={isEditingRow}>
                                                                    <Trash2 className="h-4 w-4"/>
                                                                </Button>
                                                            </AlertDialogTrigger>

                                                            <AlertDialogContent>
                                                                <AlertDialogHeader>
                                                                    <AlertDialogTitle>
                                                                        {t('entry_points.delete.title', {
                ns: 'glossary',
            })}
                                                                    </AlertDialogTitle>
                                                                    <AlertDialogDescription>
                                                                        {t('entry_points.delete.description', {
                ns: 'glossary',
            })}
                                                                    </AlertDialogDescription>
                                                                </AlertDialogHeader>

                                                                <AlertDialogFooter>
                                                                    <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                                                                    <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteMutation.mutate(entryPoint.publicId)} disabled={deleteMutation.isPending}>
                                                                        {t('delete')}
                                                                    </AlertDialogAction>
                                                                </AlertDialogFooter>
                                                            </AlertDialogContent>
                                                        </AlertDialog>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('delete')}</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>)))}
                        </TableBody>
                    </Table>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={() => setOpen(false)}>
                        {t('close')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>);
};
